import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TablePagination,
    TextField,
    Typography,
    Tabs,
    Tab,
    Grid,
    Card,
    CardContent,
    Divider,
    Link,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
    getSpecialistRegistrations,
    getSpecialistRegistrationById,
    updateSpecialistRegistrationStatus,
    getSpecialistRegistrationStats,
} from "../../../api/services/api";
import EmptyScreen from "../../../components/EmptyScreen";
import Loader from "../../../components/Loader";
import Table from "../../../components/TableComponent";
import { RoleGuard } from "../../../components/RBAC";
import { useNavigate } from "react-router-dom";

export const SpecialistRegistrations = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [limit, setLimit] = useState<number>(10);
    const [page, setPage] = useState<number>(0);
    const [tabValue, setTabValue] = useState(0);
    const [selectedRegistration, setSelectedRegistration] = useState<any>(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [newStatus, setNewStatus] = useState("");
    const [reviewNotes, setReviewNotes] = useState("");

    const statusFilter =
        tabValue === 0
            ? "PENDING"
            : tabValue === 1
                ? "UNDER_REVIEW"
                : tabValue === 2
                    ? "APPROVED"
                    : tabValue === 3
                        ? "REJECTED"
                        : "CONTACTED";

    // Fetch statistics
    const { data: statsData } = useQuery(
        ["specialist-registration-stats"],
        getSpecialistRegistrationStats
    );

    // Fetch registrations
    const { data, isLoading } = useQuery(
        [
            "specialist-registrations",
            {
                search: search,
                limit: limit,
                offset: limit * page,
                status: statusFilter,
            },
        ],
        getSpecialistRegistrations
    );

    const { mutate: updateStatusMutate } = useMutation(
        updateSpecialistRegistrationStatus,
        {
            onSuccess: (res: any) => {
                queryClient.invalidateQueries("specialist-registrations");
                queryClient.invalidateQueries("specialist-registration-stats");
                toast.success("Status updated successfully");
                setStatusDialogOpen(false);
                setViewDialogOpen(false);
                setReviewNotes("");
            },
            onError: (res: any) => {
                toast.error(res?.response?.data?.message || "Failed to update status");
            },
        }
    );

    const handlePageChange = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event: any) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleViewClick = (registration: any) => {
        setSelectedRegistration(registration);
        setViewDialogOpen(true);
    };

    const handleStatusChange = (status: string) => {
        setNewStatus(status);
        setStatusDialogOpen(true);
    };

    const confirmStatusChange = () => {
        if (selectedRegistration && newStatus) {
            updateStatusMutate({
                id: selectedRegistration.id,
                status: newStatus,
                review_notes: reviewNotes || undefined,
            });
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "APPROVED":
                return "success";
            case "PENDING":
                return "warning";
            case "UNDER_REVIEW":
                return "info";
            case "REJECTED":
                return "error";
            case "CONTACTED":
                return "primary";
            default:
                return "default";
        }
    };

    const Columns = [
        {
            title: "Full Name",
            dataIndex: "full_name",
            key: "full_name",
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "Mobile",
            dataIndex: "mobile_number",
            key: "mobile_number",
        },
        {
            title: "Specialty",
            dataIndex: "specialty",
            key: "specialty",
            render: (colData: any) => {
                if (Array.isArray(colData)) {
                    return colData.slice(0, 2).join(", ") + (colData.length > 2 ? "..." : "");
                }
                return colData || "N/A";
            },
        },
        {
            title: "Experience",
            dataIndex: "years_of_experience",
            key: "years_of_experience",
            render: (colData: any) => `${colData} years`,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (colData: any) => (
                <Chip
                    label={colData?.replace("_", " ") || "Pending"}
                    color={getStatusColor(colData)}
                    size="small"
                />
            ),
        },
        {
            title: "Submitted",
            dataIndex: "created_at",
            key: "created_at",
            render: (colData: any) =>
                new Date(colData).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                }),
        },
        {
            title: "Actions",
            dataIndex: "id",
            key: "id",
            render: (colData: any, row: any) => (
                <Box display="flex" gap="10px">
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleViewClick(row)}
                    >
                        <VisibilityIcon />
                    </IconButton>
                </Box>
            ),
        },
    ];

    const stats = statsData?.data?.data || {};

    return (
        <Box>
            {/* Header with Stats */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
            >
                <Typography variant="h4" fontWeight="bold">
                    Specialist Registrations
                </Typography>
                <RoleGuard allowedRoles={["SUPER_ADMIN", "ADMIN"]}>
                    <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={() => navigate("/admin/specialist-registrations/create")}
                    >
                        Add Specialist
                    </Button>
                </RoleGuard>
            </Box>

            {/* Statistics Cards */}
            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="body2">
                                Pending
                            </Typography>
                            <Typography variant="h4" color="warning.main">
                                {stats.pending || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="body2">
                                Under Review
                            </Typography>
                            <Typography variant="h4" color="info.main">
                                {stats.underReview || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="body2">
                                Approved
                            </Typography>
                            <Typography variant="h4" color="success.main">
                                {stats.approved || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="body2">
                                Rejected
                            </Typography>
                            <Typography variant="h4" color="error.main">
                                {stats.rejected || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="body2">
                                Total
                            </Typography>
                            <Typography variant="h4">{stats.total || 0}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Search and Tabs */}
            <Box mb={3}>
                <TextField
                    placeholder="Search by name, email, or mobile"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0);
                    }}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: "gray" }} />,
                    }}
                    sx={{ width: "100%", maxWidth: "400px", mb: 2 }}
                />

                <Tabs
                    value={tabValue}
                    onChange={(e, newValue) => {
                        setTabValue(newValue);
                        setPage(0);
                    }}
                    sx={{ borderBottom: 1, borderColor: "divider" }}
                >
                    <Tab label="Pending" />
                    <Tab label="Under Review" />
                    <Tab label="Approved" />
                    <Tab label="Rejected" />
                    <Tab label="Contacted" />
                </Tabs>
            </Box>

            {/* Table */}
            {isLoading ? (
                <Loader />
            ) : !data?.data?.data?.length ? (
                <EmptyScreen message="No registrations found" />
            ) : (
                <>
                    <Table
                        columns={Columns}
                        dataSource={data?.data?.data || []}

                    />
                    <TablePagination
                        component="div"
                        count={data?.data?.pagination?.total || 0}
                        page={page}
                        onPageChange={handlePageChange}
                        rowsPerPage={limit}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                </>
            )}

            {/* View Details Dialog */}
            <Dialog
                open={viewDialogOpen}
                onClose={() => setViewDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Specialist Registration Details</Typography>
                        <Chip
                            label={selectedRegistration?.status?.replace("_", " ")}
                            color={getStatusColor(selectedRegistration?.status)}
                        />
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedRegistration && (
                        <Grid container spacing={3}>
                            {/* Personal Information */}
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom color="primary">
                                    Personal Information
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Full Name
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {selectedRegistration.full_name}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    ID/Residence Number
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {selectedRegistration.id_number}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Date of Birth
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {new Date(
                                        selectedRegistration.date_of_birth
                                    ).toLocaleDateString()}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Nationality
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {selectedRegistration.nationality}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Gender
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {selectedRegistration.gender}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Mobile Number
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {selectedRegistration.mobile_number}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Email
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {selectedRegistration.email}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Address
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {selectedRegistration.address}
                                </Typography>
                            </Grid>

                            {/* Professional Information */}
                            <Grid item xs={12} mt={2}>
                                <Typography variant="h6" gutterBottom color="primary">
                                    Professional Information
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    Specialty
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                                    {selectedRegistration.specialty?.map(
                                        (spec: string, index: number) => (
                                            <Chip key={index} label={spec} size="small" />
                                        )
                                    )}
                                </Box>
                            </Grid>

                            {selectedRegistration.other_specialty && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary">
                                        Other Specialty
                                    </Typography>
                                    <Typography variant="body1" fontWeight="500">
                                        {selectedRegistration.other_specialty}
                                    </Typography>
                                </Grid>
                            )}

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Professional Classification
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {selectedRegistration.professional_classification}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Licence Number
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {selectedRegistration.licence_number}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Issuing Authority
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {selectedRegistration.issuing_authority}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Licence Expiry Date
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {new Date(
                                        selectedRegistration.licence_expiry_date
                                    ).toLocaleDateString()}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Years of Experience
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {selectedRegistration.years_of_experience} years
                                </Typography>
                            </Grid>

                            {/* Documents */}
                            {(selectedRegistration.licence_document_url ||
                                selectedRegistration.id_document_url ||
                                selectedRegistration.cv_document_url) && (
                                    <>
                                        <Grid item xs={12} mt={2}>
                                            <Typography variant="h6" gutterBottom color="primary">
                                                Uploaded Documents
                                            </Typography>
                                            <Divider sx={{ mb: 2 }} />
                                        </Grid>

                                        {selectedRegistration.licence_document_url && (
                                            <Grid item xs={12} sm={4}>
                                                <Link
                                                    href={selectedRegistration.licence_document_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    View Licence Document
                                                </Link>
                                            </Grid>
                                        )}

                                        {selectedRegistration.id_document_url && (
                                            <Grid item xs={12} sm={4}>
                                                <Link
                                                    href={selectedRegistration.id_document_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    View ID Document
                                                </Link>
                                            </Grid>
                                        )}

                                        {selectedRegistration.cv_document_url && (
                                            <Grid item xs={12} sm={4}>
                                                <Link
                                                    href={selectedRegistration.cv_document_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    View CV/Resume
                                                </Link>
                                            </Grid>
                                        )}
                                    </>
                                )}

                            {/* Review Information */}
                            {selectedRegistration.reviewed_by && (
                                <>
                                    <Grid item xs={12} mt={2}>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Review Information
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="textSecondary">
                                            Reviewed By
                                        </Typography>
                                        <Typography variant="body1" fontWeight="500">
                                            {selectedRegistration.users?.full_name || "N/A"}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" color="textSecondary">
                                            Reviewed At
                                        </Typography>
                                        <Typography variant="body1" fontWeight="500">
                                            {new Date(
                                                selectedRegistration.reviewed_at
                                            ).toLocaleString()}
                                        </Typography>
                                    </Grid>

                                    {selectedRegistration.review_notes && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="textSecondary">
                                                Review Notes
                                            </Typography>
                                            <Typography variant="body1" fontWeight="500">
                                                {selectedRegistration.review_notes}
                                            </Typography>
                                        </Grid>
                                    )}
                                </>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
                    <RoleGuard allowedRoles={["SUPER_ADMIN", "ADMIN"]}>
                        <Box display="flex" gap={1}>
                            {selectedRegistration?.status !== "UNDER_REVIEW" && (
                                <Button
                                    variant="outlined"
                                    color="info"
                                    onClick={() => handleStatusChange("UNDER_REVIEW")}
                                >
                                    Mark Under Review
                                </Button>
                            )}
                            {selectedRegistration?.status !== "CONTACTED" && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => handleStatusChange("CONTACTED")}
                                >
                                    Mark Contacted
                                </Button>
                            )}
                            {selectedRegistration?.status !== "APPROVED" && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={() => handleStatusChange("APPROVED")}
                                >
                                    Approve
                                </Button>
                            )}
                            {selectedRegistration?.status !== "REJECTED" && (
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={() => handleStatusChange("REJECTED")}
                                >
                                    Reject
                                </Button>
                            )}
                        </Box>
                    </RoleGuard>
                </DialogActions>
            </Dialog>

            {/* Status Change Dialog */}
            <Dialog
                open={statusDialogOpen}
                onClose={() => setStatusDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Update Registration Status</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" mb={2}>
                        Are you sure you want to change the status to{" "}
                        <strong>{newStatus?.replace("_", " ")}</strong>?
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Review Notes (Optional)"
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        placeholder="Add any comments or notes about this decision..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={confirmStatusChange}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
